/**
 * AddTestCaseModal
 * Feature 7 — Manual test case creation form shown as a Bootstrap modal.
 */

import React, { useState, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { XLg } from 'react-bootstrap-icons';

import { addManualTestCase, AddManualTestCasePayload } from '../../api/requirements';
import { RequirementTestFolderNode } from '../../types/requirements';
import FolderPathSelector, { normalizeFolderPathInput } from './FolderPathSelector';

interface StepDraft {
  action: string;
  expected_result: string;
}

interface AddTestCaseModalProps {
  projectId: number | string;
  generationId: number | string;
  artifactId: number | string;
  directory?: RequirementTestFolderNode | null;
  onClose: () => void;
  onCreated: (newTestCase: Record<string, any>) => void;
}

function AddTestCaseModal({
  projectId,
  generationId,
  artifactId,
  directory,
  onClose,
  onCreated,
}: AddTestCaseModalProps): React.ReactElement {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [steps, setSteps] = useState<StepDraft[]>([{ action: '', expected_result: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStepChange = useCallback(
    (index: number, field: 'action' | 'expected_result', value: string) => {
      setSteps((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    [],
  );

  const handleAddStep = useCallback(() => {
    setSteps((prev) => [...prev, { action: '', expected_result: '' }]);
  }, []);

  const handleRemoveStep = useCallback((index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!title.trim()) {
        setError('Title is required.');
        return;
      }

      setSaving(true);
      setError(null);

      const folderSegments = normalizeFolderPathInput(folderPath);

      const payload: AddManualTestCasePayload = {
        title: title.trim(),
        description: description.trim() || undefined,
        steps: steps
          .filter((s) => s.action.trim())
          .map((s) => ({ action: s.action.trim(), expected_result: s.expected_result.trim() })),
        priority: priority.trim() || undefined,
        folder_path: folderSegments.length > 0 ? folderSegments : undefined,
      };

      try {
        const created = await addManualTestCase(projectId, generationId, artifactId, payload);
        onCreated(created);
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to create test case.');
        setSaving(false);
      }
    },
    [title, description, priority, folderPath, steps, projectId, generationId, artifactId, onCreated],
  );

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>Add Test Case</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit} noValidate id="add-tc-form">
          {/* Title */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.84rem', fontWeight: 600 }}>
              Title <span style={{ color: '#dc3545' }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of what this test validates"
              disabled={saving}
              size="sm"
            />
          </Form.Group>

          {/* Description */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.84rem', fontWeight: 600 }}>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed objective of this test case (optional)"
              disabled={saving}
              style={{ fontSize: '0.84rem', resize: 'vertical' }}
            />
          </Form.Group>

          {/* Priority & Folder in a row */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label style={{ fontSize: '0.84rem', fontWeight: 600 }}>Priority</Form.Label>
              <Form.Select
                size="sm"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                disabled={saving}
              >
                <option value="">Select priority…</option>
                <option value="1">1 — Critical</option>
                <option value="2">2 — High</option>
                <option value="3">3 — Medium</option>
                <option value="4">4 — Low</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ flex: 2 }}>
              <Form.Label style={{ fontSize: '0.84rem', fontWeight: 600 }}>
                Folder path
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6, fontSize: '0.75rem' }}>
                  (slash-separated, optional)
                </span>
              </Form.Label>
              <FolderPathSelector
                value={folderPath}
                onChange={setFolderPath}
                directory={directory}
                placeholder="All Test Cases / Manual / Authentication"
                disabled={saving}
              />
            </Form.Group>
          </div>

          {/* Steps */}
          <div>
            <p style={{ fontSize: '0.84rem', fontWeight: 600, marginBottom: 8 }}>Steps</p>
            {steps.map((step, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  marginBottom: 10,
                  padding: '10px 12px',
                  background: '#f9fafb',
                  borderRadius: 8,
                  border: '1px solid #e8ecf0',
                }}
              >
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', minWidth: 44, paddingTop: 6 }}>
                  Step {index + 1}
                </span>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Action"
                    value={step.action}
                    onChange={(e) => handleStepChange(index, 'action', e.target.value)}
                    disabled={saving}
                    style={{ fontSize: '0.83rem', resize: 'vertical' }}
                  />
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Expected result"
                    value={step.expected_result}
                    onChange={(e) => handleStepChange(index, 'expected_result', e.target.value)}
                    disabled={saving}
                    style={{ fontSize: '0.83rem', resize: 'vertical' }}
                  />
                </div>
                {steps.length > 1 && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => handleRemoveStep(index)}
                    disabled={saving}
                    style={{ padding: 4, color: '#9ca3af', flexShrink: 0 }}
                    title="Remove step"
                  >
                    <XLg size={12} />
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleAddStep}
              disabled={saving}
              style={{ fontSize: '0.82rem' }}
            >
              + Add Step
            </Button>
          </div>

          {error && (
            <div style={{ marginTop: 14, fontSize: '0.83rem', color: '#dc3545' }}>{error}</div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" form="add-tc-form" disabled={saving || !title.trim()}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" style={{ marginRight: 6 }} />
              Creating…
            </>
          ) : (
            'Create Test Case'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddTestCaseModal;
